import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import ReactGA from "react-ga4";
import { AnimatePresence, motion } from "framer-motion";

import {
  Box, Button, FormControl, FormLabel, Input, Stack,
  Text, Textarea, useColorModeValue, useToast, VStack,
} from "@chakra-ui/react";
import PageLayout, { PageHeader } from "../components/layout/PageLayout";
import NetlifyHoneypot from "../components/common/NetlifyHoneypot";
import NetlifyRecaptcha, { isRecaptchaEnabled } from "../components/common/NetlifyRecaptcha";
import { TYPE } from "../styles/theme";

const ContactPage = () => {
  const navigate = useNavigate();
  const toast = useToast();
  const [submitted, setSubmitted] = useState(false);
  const [sending, setSending] = useState(false);
  const [captchaToken, setCaptchaToken] = useState("");

  const surface = useColorModeValue("#fdfaf5", "#1e1510");
  const border  = useColorModeValue("rgba(0,0,0,0.08)", "rgba(255,255,255,0.07)");
  const text    = useColorModeValue("#1a0c04", "#e8d9c4");
  const dim     = useColorModeValue("#6a4020", "#a89880");
  const accent  = useColorModeValue("#8b3e12", "#c8a97e");

  useEffect(() => {
    ReactGA.send({ hitType: "pageview", page: window.location.pathname + window.location.search, title: "contact-form", location: window.location.href });
  }, []);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (isRecaptchaEnabled && !captchaToken) {
      toast({
        title: "Complete the captcha first.",
        status: "warning",
        duration: 4000,
        isClosable: true,
      });
      return;
    }

    setSending(true);
    const body = new URLSearchParams(new FormData(e.target));
    if (captchaToken) body.set("g-recaptcha-response", captchaToken);

    fetch("/", {
      method: "POST",
      headers: { "Content-Type": "application/x-www-form-urlencoded" },
      body: body.toString(),
    })
      .then((res) => {
        if (!res.ok) throw new Error(`${res.status}`);
        setSubmitted(true);
        ReactGA.event({ category: "Form", action: "Submit Success", label: "Contact Form" });
      })
      .catch(() => {
        toast({
          title: "Couldn't send your message.",
          description: "Something went wrong. Try again in a moment.",
          status: "error",
          duration: 6000,
          isClosable: true,
        });
      })
      .finally(() => setSending(false));
  };

  return (
    <PageLayout narrow>
        <PageHeader
          title="Contact & support"
          subtitle="App questions, missing shops, or a correction to your café’s details. For app issues, include your device and what happened."
        />

        <AnimatePresence mode="wait">
          {submitted ? (
            <motion.div
              key="success"
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3, ease: [0.16, 1, 0.3, 1] }}
            >
              <Box
                bg={surface} border="1px solid" borderColor={border}
                borderRadius="20px" p={{ base: 8, md: 10 }} textAlign="center"
              >
                <Text fontSize="32px" mb={3}>☕</Text>
                <Text as="h2" fontFamily="heading" fontSize={TYPE.sectionTitle} fontWeight={400} color={text} mb={3}>
                  Message sent.
                </Text>
                <Text fontSize={TYPE.body} color={dim} lineHeight={1.7} mb={6}>
                  Thanks for reaching out. We'll read it - replies aren't always instant.
                </Text>
                <Text
                  as="button" type="button" fontSize={TYPE.meta} minH="44px" color={accent} cursor="pointer"
                  _hover={{ opacity: 0.75 }} transition="opacity 0.12s ease"
                  onClick={() => navigate("/")}
                >
                  Back to Kuthé
                </Text>
              </Box>
            </motion.div>
          ) : (
            <motion.div
              key="form"
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3, ease: [0.16, 1, 0.3, 1] }}
            >
              <Box
                as="form"
                position="relative"
                bg={surface} border="1px solid" borderColor={border}
                borderRadius="20px" p={{ base: 4, md: 5 }}
                onSubmit={handleSubmit}
                data-netlify="true"
                data-netlify-honeypot="bot-field"
                name="contact"
                method="POST"
              >
                <input type="hidden" name="form-name" value="contact" />
                <NetlifyHoneypot />

                <VStack spacing={4}>
                  <Stack direction={{ base: "column", sm: "row" }} spacing={3} w="full">
                    <FormControl isRequired>
                      <FormLabel fontSize={TYPE.meta} color={dim} mb="2px" fontFamily="body">First name</FormLabel>
                      <Input fontSize="16px" name="firstName" placeholder="Jane" />
                    </FormControl>
                    <FormControl isRequired>
                      <FormLabel fontSize={TYPE.meta} color={dim} mb="2px" fontFamily="body">Last name</FormLabel>
                      <Input fontSize="16px" name="lastName" placeholder="Doe" />
                    </FormControl>
                  </Stack>

                  <FormControl isRequired>
                    <FormLabel fontSize={TYPE.meta} color={dim} mb="2px" fontFamily="body">Email</FormLabel>
                    <Input fontSize="16px" name="email" type="email" placeholder="jane@example.com" />
                  </FormControl>

                  <FormControl isRequired>
                    <FormLabel fontSize={TYPE.meta} color={dim} mb="2px" fontFamily="body">Message</FormLabel>
                    <Textarea name="message" fontSize="16px" placeholder="What’s on your mind?" rows={5} resize="vertical" />
                  </FormControl>

                  <NetlifyRecaptcha onChange={setCaptchaToken} />

                  <Button type="submit" w="full" isLoading={sending} isDisabled={isRecaptchaEnabled && !captchaToken}>
                    Send message
                  </Button>
                </VStack>
              </Box>
            </motion.div>
          )}
        </AnimatePresence>

    </PageLayout>
  );
};

export default ContactPage;
